import {
   Component,
   AfterViewInit,
   ViewChild,
   ElementRef,
   Input,
   OnInit,
} from '@angular/core';
import { Chart, TooltipItem } from 'chart.js';
import { StockFilterService } from 'src/app/features/stock-screener/services/stock-filter.service';
import { Hit } from 'src/app/models/sector-industries/sector-industries.model';
import { ChangeDetectorRef } from '@angular/core';

@Component({
    selector: 'app-ratio-annual-chart',
    templateUrl: './ratio-annual-chart.component.html',
    styleUrls: ['./ratio-annual-chart.component.scss'],
    standalone: false
})
export class RatioAnnualChartComponent implements OnInit {
   allDataAnnual: any[] = [];
   @Input() chartKey: string = '';
   @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
   chart!: Chart;
   pte: any[] = [];
   ptb: any[] = [];
   @Input() price_to_earning: any[] = []; // Dynamic input for Price to Earnings
   private chartInitialized = false;

   noData: boolean = false;

   constructor(
      private financialService: StockFilterService,
      private cd: ChangeDetectorRef
   ) { }

   async ngOnInit() {
      // Fetch data and check if there's no data
      await Promise.all([this.getActualAnnualData(['pb'], 'annual', this.ptb)]);
      this.noData = this.ptb.every((value) => value === null);

      // Update the chart if data is available and chart is initialized
      if (!this.noData && this.chartInitialized) {
         this.updateChart();
      }
   }

   ngAfterViewInit() {
      // Initialize the chart after the view is fully initialized
      this.initializeChart();
   }

   private initializeChart() {
      const ctx = this.chartCanvas.nativeElement.getContext('2d');
      if (ctx) {
         const groupedData = {
            labels: ['2020', '2021', '2022', '2023', '2024'], // Categories for groups
            datasets: [
               {
                  label: 'Price to Book',
                  data: this.ptb, // Use dynamic data for Price to Book
                  backgroundColor: 'rgba(48, 131, 255, 1)',
                  borderRadius: 10,
               },
               {
                  label: 'Price to Earnings',
                  data: this.price_to_earning, // Use dynamic data for Price to Earnings
                  backgroundColor: 'rgba(112, 218, 208, 1)',
                  borderRadius: 10,
               },
            ],
         };

         this.chart = new Chart(ctx, {
            type: 'bar',
            data: groupedData,
            options: {
               responsive: true,
               maintainAspectRatio: false,
               layout: {
                  padding: {
                     top: 30,
                  },
               },
               plugins: {
                  legend: {
                     display: true, // Show the legend to identify grouped bars
                     position: 'bottom',
                     align: 'start',
                     labels: {
                        usePointStyle: true, // Use custom point style for legend markers
                        boxWidth: 8, // Set smaller size for legend markers
                        padding: 40,
                     },
                  },
                  // @ts-ignore
                  datalabels: {
                     anchor: 'end',
                     align: 'top',
                     display: false,
                     formatter: (value: number) => `${value}`, // Show raw value
                     font: {
                        size: 14,
                        weight: 400,
                        family: 'Poppins, sans-serif',
                     },
                     color: '#000',
                  },
               },
               scales: {
                  x: {
                     display: true,
                     grid: {
                        display: false,
                     },
                  },
                  y: {
                     display: true,
                     position: 'right',
                     ticks: {
                        callback: (value: any) => `${value}`,
                     },
                     grid: {
                        display: true,
                        color: '#FFFFFF',
                        lineWidth: 3,
                        drawBorder: false
                     },
                  },
               },
            },
         });
         this.chartInitialized = true; // Mark the chart as initialized
      }
   }

   private updateChart() {
      if (this.chart?.data?.datasets?.length) {
         // Update the Price to Earnings dataset
         this.chart.data.datasets[1].data = this.price_to_earning;
         // Update the Price to Book dataset
         this.chart.data.datasets[0].data = this.ptb;
         this.chart.update(); // Refresh the chart
      }
   }

   async getActualAnnualData(
      keys: string[],
      timePeriod: string,
      targetArray: number[]
   ): Promise<void> {
      try {
         const ticker = this.chartKey;
         const targetYears = [2020, 2021, 2022, 2023, 2024];
         let page = 1; // Start with page 1
         let perPage = 250; // Increased from 50 to 250
         let hasMoreData = true;
         let allHits: Hit[] = [];

         while (hasMoreData) {
            // Fetch data using the entire `keys` array
            const response = await this.financialService.getRatAnnualData2(
               ticker,
               keys,
               timePeriod,
               perPage,
               page
            );

            if (response?.hits) {
               allHits.push(...response.hits); // Collect all hits
            }

            // Check if more pages are available
            hasMoreData = response?.hits.length === perPage;
            page++; // Move to next page
         }

         // Initialize array with `0` for all target years
         const yearValues: number[] = new Array(targetYears.length).fill(0);

         // Process and map the data after collecting all pages
         allHits.forEach((hit: Hit) => {
            const period = new Date(hit.document.period);
            const year = period.getFullYear();
            const index = targetYears.indexOf(year);

            if (index !== -1) {
               yearValues[index] = hit.document.v ?? 0; // Replace with actual value or keep 0
            }
         });

         // Update the targetArray with the final values
         targetArray.splice(0, targetArray.length, ...yearValues);

         //console.log("Updated Annual Values:", targetArray);
         this.cd.detectChanges();
      } catch (error) {
         //console.error("Error fetching data:", error);
         throw error;
      }
   }
}
